import { useEffect, useRef } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { FiCheckCircle, FiLoader, FiCreditCard, FiXCircle } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { verifyCheckoutSession } from "../../store/smileDesign/paymentSlice";
import { fetchUser } from "../../store/common/authSlice";
import { getCreditUi } from "../../constants/creditPageTheme";

const PaymentSuccess = () => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const sessionId = searchParams.get("session_id");
    const isPhotoRecolour = location.pathname.includes("photo-recolour");
    const t = getCreditUi(isPhotoRecolour ? "light" : "dark");
    const packagesPath = isPhotoRecolour
        ? "/dashboard/photo-recolour/packages"
        : "/dashboard/smiledesign/list-credits";
    const continuePath = isPhotoRecolour
        ? "/dashboard/photo-recolour"
        : "/dashboard/smiledesign/generate";
    const ordersPath = isPhotoRecolour
        ? "/dashboard/photo-recolour/order-history"
        : "/dashboard/smiledesign/my-orders";
    const dispatch = useAppDispatch();
    const hasVerifiedRef = useRef(false);

    const { verifyLoading, verificationResult, error } = useAppSelector(
        (state) => state.payment
    );

    useEffect(() => {
        if (!sessionId || hasVerifiedRef.current) return;

        hasVerifiedRef.current = true;

        dispatch(verifyCheckoutSession(sessionId)).then((result) => {
            if (verifyCheckoutSession.fulfilled.match(result)) {
                dispatch(fetchUser());
            }
        });
    }, [dispatch, sessionId]);

    const order = verificationResult?.order;
    const creditsGranted = verificationResult?.credits_granted;
    const sessionStatus = verificationResult?.session_status;
    const isPaid =
        order?.status === "PAID" && sessionStatus === "paid" && !error;

    return (
        <div className={`${t.page} flex items-center justify-center min-h-[70vh]`}>
            <div className={`w-full max-w-lg ${t.card} p-5 sm:p-8 text-center`}>
                {!sessionId && (
                    <div className="py-6">
                        <p className={`${t.danger} mb-6`}>
                            Invalid payment session. No session ID was provided.
                        </p>
                        <Link
                            to={packagesPath}
                            className={`inline-block px-6 py-3 ${t.primaryBtn}`}
                        >
                            Back to Packages
                        </Link>
                    </div>
                )}

                {sessionId && verifyLoading && (
                    <div className="flex flex-col items-center py-10">
                        <FiLoader className={`${t.loader} text-5xl mb-4`} />
                        <p className={t.muted}>Confirming your payment...</p>
                    </div>
                )}

                {sessionId && !verifyLoading && (error || (order && !isPaid)) && (
                    <div className="py-6">
                        <div className="flex justify-center mb-6">
                            <div
                                className={`w-20 h-20 rounded-full flex items-center justify-center ${
                                    isPhotoRecolour
                                        ? "bg-red-50 border border-red-200"
                                        : "bg-red-500/15 border border-red-500/30"
                                }`}
                            >
                                <FiXCircle className={`${t.danger} text-4xl`} />
                            </div>
                        </div>
                        <h1 className={`text-2xl font-bold ${t.body} mb-2`}>
                            Payment Not Completed
                        </h1>
                        <p className={`${t.danger} mb-6`}>
                            {error ||
                                "Your payment could not be confirmed. No charges were applied."}
                        </p>
                        <Link
                            to={packagesPath}
                            className={`inline-block px-6 py-3 ${t.primaryBtn}`}
                        >
                            Back to Packages
                        </Link>
                    </div>
                )}

                {sessionId && !verifyLoading && isPaid && order && (
                    <>
                        <div className="flex justify-center mb-6">
                            <div
                                className={`w-20 h-20 rounded-full flex items-center justify-center ${
                                    isPhotoRecolour
                                        ? "bg-emerald-50 border border-emerald-200"
                                        : "bg-green-500/15 border border-green-500/30"
                                }`}
                            >
                                <FiCheckCircle className={`${t.success} text-4xl`} />
                            </div>
                        </div>

                        <h1 className={`text-3xl font-bold ${t.body} mb-2`}>
                            Payment Successful
                        </h1>

                        <p className={`${t.muted} mb-8`}>
                            Your credits have been added to your account.
                        </p>

                        <div className={`${t.innerCard} p-5 text-left space-y-3 mb-8`}>
                            <div className="flex justify-between">
                                <span className={t.muted}>Order</span>
                                <span className={`${t.accent} font-medium`}>
                                    #{order.order_id}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className={t.muted}>Package</span>
                                <span className={t.body}>{order.package_name}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className={t.muted}>Amount</span>
                                <span className={`${t.success} font-semibold`}>
                                    {order.currency}
                                    {order.amount}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className={t.muted}>Credits Added</span>
                                <span className={`flex items-center gap-2 ${t.accent} font-bold`}>
                                    <FiCreditCard />
                                    +{creditsGranted}
                                </span>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                            <Link
                                to={continuePath}
                                className={`flex-1 py-3.5 text-center ${t.primaryBtn}`}
                            >
                                Start Generating
                            </Link>
                            <Link
                                to={ordersPath}
                                className={`flex-1 py-3.5 text-center ${t.secondaryBtn}`}
                            >
                                View Order History
                            </Link>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default PaymentSuccess;
